import React, { useState } from "react";
import apiClient from "../../service/ApiClient";
import { S3_BASE_URL } from "../../utils/Constants";

/**
 * FileUploader component
 *
 * Props:
 *  - label?: string
 *  - onUploadComplete: (key: string) => void
 *  - uploadingText?: string
 *  - t?: (key: string) => string
 *  - previewEnabled?: boolean
 *  - initialKey?: string
 */
export function FileUploader({
  label,
  onUploadComplete,
  uploadingText = "Uploading...",
  t = (key) => key,
  previewEnabled = false,
  initialKey = "",
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedKey, setUploadedKey] = useState(initialKey);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const resp = await apiClient.get(
        `/s3/presigned-url?filename=${encodeURIComponent(
          file.name
        )}`
      );
      const { url, key } = resp.data;
      const uploadRes = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadRes.ok) {
        throw new Error(`Upload failed with status ${uploadRes.status}`);
      }
      onUploadComplete(key);
      if (previewEnabled) setUploadedKey(key);
    } catch (err) {
      console.error("File upload error:", err);
      alert(t("uploadError"));
      onUploadComplete("");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      <input
        type="file"
        accept="image/*"
        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 rounded-md cursor-pointer"
        onChange={handleChange}
        disabled={isUploading}
      />
      {isUploading && (
        <div className="mt-2 flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm text-gray-600">{t(uploadingText)}</span>
        </div>
      )}
      {previewEnabled && uploadedKey && (
        <div className="mt-3">
          <img
            src={`${S3_BASE_URL}/${uploadedKey}`}
            alt="Uploaded preview"
            className="h-24 w-auto object-cover rounded-md border border-gray-200"
          />
        </div>
      )}
    </div>
  );
}